/**
 * Analytics — ABM Site Risk Insights
 * ───────────────────────────────────
 * Premium reinsurer-ready dashboard.
 *
 *   • Consolidated payload from `/analytics/risk-insights`.
 *   • Currency, dates and number formatting driven by Settings → Regional.
 *   • Drill-down: clicking a pie slice / ABM bar / worst-category row /
 *     scatter dot / map marker sets a page-level filter chip that
 *     re-filters ALL widgets and recomputes KPIs client-side.
 *   • Export PDF: branded A4 report with cover header, filter summary,
 *     KPI grid, chart snapshots and a page-number footer.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input, Label, Select } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, LabelList,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, useMap } from 'react-leaflet';
/* eslint-disable-next-line */
const MarkerClusterGroup = require('react-leaflet-cluster').default as React.ComponentType<{
  chunkedLoading?: boolean;
  spiderfyOnMaxZoom?: boolean;
  showCoverageOnHover?: boolean;
  maxClusterRadius?: number;
  disableClusteringAtZoom?: number;
  iconCreateFunction?: (cluster: { getChildCount: () => number; getAllChildMarkers: () => Array<{ options?: { bandKey?: string } }> }) => unknown;
  children?: React.ReactNode;
}>;
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import {
  MapPin, AlertTriangle, TrendingUp, Wallet, Shield, Layers, RefreshCw,
  Download, X, Filter, Compass, Satellite,
} from 'lucide-react';
import { useRegional, formatCurrency } from '@/lib/regional';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SiteInsightsModal } from './SiteInsightsModal';
import { CustomiseHost } from './dashboard-builder/CustomiseHost';

/* ─── Types ─────────────────────────────────────────────────────────── */
interface KPIs { site_count: number; total_cash_exposure: number; avg_cash_exposure: number; avg_risk_score: number; high_critical_count: number; high_critical_pct: number }
interface BandCount { band: string; count: number }
interface MapPoint { lat: number; lng: number; band: string; score: number; site_name: string; site_id?: string; abm_type?: string; cash_exposure?: number }
interface TopSite { site_id: string; site_name: string; town: string; abm_type: string; band: string; score: number; cash_exposure: number }
interface ScatterPoint { x: number; y: number; band: string; site_name: string; site_id?: string; abm_type?: string }
interface AbmRow { abm_type: string; sites: number; Low: number; Medium: number; High: number; Critical: number; total_cash: number }
interface WorstCat { category: string; flagged_count: number }
interface SecurityFocus { totals: { yes: number; no: number; na: number }; pass_rate_pct: number; per_question: Array<{ question_code: string; yes: number; no: number; na: number }> }
interface UnsurveyedPoint { lat: number; lng: number; site_name: string; site_id: string }
interface Insights { kpis: KPIs; risk_distribution: BandCount[]; map_points: MapPoint[]; unsurveyed_points?: UnsurveyedPoint[]; top_sites: TopSite[]; scatter: ScatterPoint[]; abm_types: AbmRow[]; worst_categories: WorstCat[]; security_focus: SecurityFocus }
interface MapMarker { lat: number; lng: number; band: string; score: number; site_name: string; site_id?: string; unsurveyed: boolean }
interface Lookup { id: string; name: string; trading_name?: string; broker_id?: string; client_id?: string }

type Band = 'Low' | 'Medium' | 'High' | 'Critical';
interface Drill { band?: Band; abm_type?: string; site_id?: string; category?: string }

/* ─── Band palette + helpers ────────────────────────────────────────── */
const BAND_COLOR: Record<string, string> = {
  Low: '#10B981', Medium: '#F59E0B', High: '#EF4444', Critical: '#7F1D1D', Unknown: '#6D28D9',
};
const BANDS: Band[] = ['Low', 'Medium', 'High', 'Critical'];
const bandBadge = (b: string) => (
  <Badge style={{ backgroundColor: BAND_COLOR[b] || '#94A3B8', color: 'white' }}>{b}</Badge>
);

/**
 * Compact currency (R1.2M / R80K / R450) using regional settings so it works
 * for ZAR, USD, GBP, EUR uniformly.
 *
 * We defer to `formatCurrency` for the exact separator/positioning; here we
 * just decide the *magnitude* label.
 */
function useMoneyFormatters() {
  const { regional } = useRegional();
  const sym = regional.currency_symbol || 'R';
  const compact = useCallback((n: number): string => {
    const v = Math.round(Number(n) || 0);
    if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v.toFixed(0)}`;
  }, [sym]);
  const full = useCallback((n: number): string => formatCurrency(Number(n) || 0, regional), [regional]);
  return { compact, full, sym };
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Component
 * ═══════════════════════════════════════════════════════════════════════════ */
const DefaultAnalyticsPage: React.FC = () => {
  const [d, setD] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Lookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);
  const [brokers, setBrokers] = useState<Lookup[]>([]);
  const [templates, setTemplates] = useState<Lookup[]>([]);
  const [filter, setFilter] = useState({ broker_id: '', client_id: '', site_id: '', template_id: '', from: '', to: '' });
  const [drill, setDrill] = useState<Drill>({});
  const [exporting, setExporting] = useState(false);
  // Progressive/staged mount — rendering the 2 500-marker map AND the
  // 2 500-point scatter on the same frame froze the main thread for a few
  // seconds. We paint the shell + KPIs first (stage 0), then mount the map
  // (stage 1), then the scatter (stage 2) on separate frames so the page
  // stays interactive. Settles in <0.5s, so the PDF export (user-triggered
  // later) always finds every chart mounted.
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (stage >= 2) return;
    const id = setTimeout(() => setStage((s) => s + 1), 200);
    return () => clearTimeout(id);
  }, [stage]);
  // Site drill-in modal state — opened by clicking a map marker. Kept
  // separate from `drill` so closing the modal never disturbs the
  // aggregate analytics view or filters underneath.
  const [insightSite, setInsightSite] = useState<{
    site_id: string; site_name: string; lat: number; lng: number; band?: string; score?: number; unsurveyed?: boolean;
  } | null>(null);
  const { compact: fmtMoney, sym: currencySymbol } = useMoneyFormatters();

  /* Refs — used by the PDF exporter to snapshot each section. */
  const printRef = useRef<HTMLDivElement | null>(null);

  /* Geocoding provider state — powers the "Backfill coordinates" CTA
     shown when a live provider is enabled and there are sites missing
     lat/lng (the operator just switched from stub → Google, for example).
     `runBackfill` is wired further down once `load` exists. */
  interface MapProviderCfg {
    key: string; name: string; tile_url: string; attribution: string;
    subdomains: string[]; max_zoom: number; needs_token: boolean;
    satellite_url?: string; satellite_attribution?: string;
    satellite_subdomains?: string[]; satellite_max_zoom?: number; satellite_name?: string;
    // Street View is Google-only for now; the modal falls back to a
    // "connect Google Maps" hint when this is false.
    street_view_available?: boolean;
    street_view_url?: string | null;
    street_view_metadata_url?: string | null;
  }
  const [geoStatus, setGeoStatus] = useState<{
    provider_key: string; provider_enabled: boolean;
    sites_missing_coords: number; total_sites: number;
    map_provider?: MapProviderCfg;
  } | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [geoLoaded, setGeoLoaded] = useState(false);
  const loadGeoStatus = useCallback(async () => {
    try {
      const r = await api.get<{
        provider_key: string; provider_enabled: boolean;
        sites_missing_coords: number; total_sites: number;
        map_provider?: MapProviderCfg;
      }>('/analytics/geocoding-status');
      setGeoStatus(r.data);
    } catch { /* ignore */ }
    finally { setGeoLoaded(true); }
  }, []);
  useEffect(() => { void loadGeoStatus(); }, [loadGeoStatus]);

  /* ── Data load ────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter.broker_id) p.set('broker_id', filter.broker_id);
      if (filter.client_id) p.set('client_id', filter.client_id);
      if (filter.site_id) p.set('site_id', filter.site_id);
      if (filter.template_id) p.set('template_id', filter.template_id);
      if (filter.from) p.set('submitted_from', filter.from);
      if (filter.to) p.set('submitted_to', filter.to);
      const qs = p.toString() ? `?${p.toString()}` : '';
      const r = await api.get<Insights>(`/analytics/risk-insights${qs}`);
      setD(r.data);
    } finally { setLoading(false); }
  }, [filter.broker_id, filter.client_id, filter.site_id, filter.template_id, filter.from, filter.to]);
  useEffect(() => { void load(); }, [load]);
  /* When top filters change we wipe the drill-down. */
  useEffect(() => { setDrill({}); }, [filter.broker_id, filter.client_id, filter.site_id, filter.template_id, filter.from, filter.to]);

  /* Client + site dropdowns — deferred until the staged mount settles
     (stage 2) so the 600KB sites picker parse doesn't compete with the
     first paint + heavy map mount, keeping the page responsive on entry. */
  useEffect(() => {
    if (stage < 2) return;
    void (async () => {
      try {
        const sortBy = (arr: Lookup[]) => [...(arr || [])].sort((a, b) =>
          (a.trading_name || a.name || '').localeCompare(b.trading_name || b.name || ''));
        const [c, s, b, t] = await Promise.all([
          api.get<Lookup[]>('/clients?limit=10000'),
          api.get<Lookup[]>('/sites?limit=10000&fields=picker'),
          api.get<Lookup[]>('/brokers'),
          api.get<Lookup[]>('/templates'),
        ]);
        setClients(sortBy(c.data || []));
        setSites(sortBy(s.data || []));
        setBrokers(sortBy((b.data || []).map((x: any) => ({ id: x.id, name: x.brokerage_name || x.name }))));
        setTemplates(sortBy((t.data || []).map((x: any) => ({ id: x.id, name: x.name }))));
      } catch (err) { console.warn('analytics pickers load failed', err); }
    })();
  }, [stage]);

  /* Scope the Client + Site dropdown OPTIONS to the broker/client already
     chosen — so picking Standard Bank then a client only offers that
     broker/client's sites, not every site in the tenant. */
  const clientOptions = useMemo<Lookup[]>(
    () => (filter.broker_id ? clients.filter((c) => c.broker_id === filter.broker_id) : clients),
    [clients, filter.broker_id],
  );
  const siteOptions = useMemo<Lookup[]>(
    () => sites.filter((s) =>
      (!filter.broker_id || s.broker_id === filter.broker_id)
      && (!filter.client_id || s.client_id === filter.client_id)),
    [sites, filter.broker_id, filter.client_id],
  );

  /* Backfill trigger — uses the current geocoding provider to fill in
     lat/lng on sites that don't have them yet. Refreshes both the map
     and the status widget on success so the operator sees new dots
     immediately. */
  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const r = await api.post<{ scanned: number; updated: number; failed: number; provider: string }>(
        '/analytics/geocode-backfill?limit=500', {},
      );
      const msg = `Backfill via ${r.data.provider}: ${r.data.updated} coordinated · ${r.data.failed} failed (${r.data.scanned} scanned)`;
      if (r.data.updated > 0) {
        await load();
        await loadGeoStatus();
      }
      window.alert(msg);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      window.alert(detail ?? 'Backfill failed.');
    } finally { setBackfilling(false); }
  }, [load, loadGeoStatus]);


  /* ── Drill-down derived datasets ─────────────────────────────── */
  const hasDrill = !!(drill.band || drill.abm_type || drill.site_id || drill.category);

  /**
   * `passes` decides whether a per-site record survives the current drill-down.
   * We use it to filter map, scatter, top-10 and to recompute KPIs.
   *
   * Note the "category" drill-down is a special case — worst-categories are
   * survey-level, not site-level, so we can't hard-filter site widgets by it.
   * We instead scope the *worst-category chart itself* and leave everything
   * else in place, which reflects "this is the section you tapped".
   */
  const passes = useCallback((row: { band?: string; abm_type?: string; site_id?: string; site_name?: string }): boolean => {
    if (drill.band && row.band !== drill.band) return false;
    if (drill.abm_type && row.abm_type !== drill.abm_type) return false;
    if (drill.site_id) {
      // site_id may be missing on map/scatter → fall back to site_name.
      const sid = row.site_id || row.site_name;
      if (sid !== drill.site_id) return false;
    }
    return true;
  }, [drill.band, drill.abm_type, drill.site_id]);

  const filteredMap = useMemo<MapPoint[]>(() => (d?.map_points || []).filter(p =>
    passes({ band: p.band, abm_type: p.abm_type, site_id: p.site_id, site_name: p.site_name }),
  ), [d?.map_points, passes]);

  /* Un-surveyed but geocoded sites → neutral grey pins. Shown only when
     no drill-down is active (a band/ABM/site filter would make grey pins
     confusing). Combined with the surveyed points into one marker list. */
  const unsurveyedPoints = useMemo<UnsurveyedPoint[]>(() => d?.unsurveyed_points || [], [d?.unsurveyed_points]);
  const mapMarkers = useMemo<MapMarker[]>(() => {
    const surveyed: MapMarker[] = filteredMap.map(p => ({
      lat: p.lat, lng: p.lng, band: p.band, score: p.score,
      site_name: p.site_name, site_id: p.site_id, unsurveyed: false,
    }));
    if (hasDrill) return surveyed;
    const grey: MapMarker[] = unsurveyedPoints.map(p => ({
      lat: p.lat, lng: p.lng, band: 'Unknown', score: 0,
      site_name: p.site_name, site_id: p.site_id, unsurveyed: true,
    }));
    return [...surveyed, ...grey];
  }, [filteredMap, unsurveyedPoints, hasDrill]);

  /* ── Auto-satellite mode ────────────────────────────────────────
   * Whenever the operator narrows the view to *exactly one site* —
   * either via the top Site dropdown or by drilling into a marker /
   * top-10 row / scatter dot — we flip the basemap to that provider's
   * satellite layer and fly the camera in so property boundaries pop.
   * When there's more than one site (or none) we return to the street
   * basemap. No UI toggle needed.
   */
  const singleSitePoint = useMemo<MapPoint | undefined>(() => {
    if (filteredMap.length === 1) return filteredMap[0];
    // Also treat top-filter site_id as single-site even before results arrive.
    if (filter.site_id && filteredMap.length > 0) return filteredMap[0];
    return undefined;
  }, [filteredMap, filter.site_id]);
  const isSingleSite = !!singleSitePoint && (
    !!filter.site_id || !!drill.site_id || filteredMap.length === 1
  );
  const activeTile = useMemo(() => {
    const mp = geoStatus?.map_provider;
    if (isSingleSite && mp?.satellite_url) {
      return {
        url: mp.satellite_url,
        attribution: mp.satellite_attribution || mp.attribution,
        subdomains: (mp.satellite_subdomains && mp.satellite_subdomains.length > 0)
          ? mp.satellite_subdomains
          : (mp.subdomains && mp.subdomains.length > 0 ? mp.subdomains : ['a', 'b', 'c']),
        maxZoom: mp.satellite_max_zoom || mp.max_zoom || 19,
        name: mp.satellite_name || `${mp.name} Satellite`,
      };
    }
    return {
      url: mp?.tile_url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: mp?.attribution
        || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      subdomains: (mp?.subdomains && mp.subdomains.length > 0) ? mp.subdomains : ['a', 'b', 'c'],
      maxZoom: mp?.max_zoom || 19,
      name: mp?.name || 'OpenStreetMap',
    };
  }, [geoStatus?.map_provider, isSingleSite]);

  const filteredTop = useMemo<TopSite[]>(() => (d?.top_sites || []).filter(p =>
    passes({ band: p.band, abm_type: p.abm_type, site_id: p.site_id, site_name: p.site_name }),
  ), [d?.top_sites, passes]);

  const filteredScatter = useMemo<ScatterPoint[]>(() => (d?.scatter || []).filter(p =>
    passes({ band: p.band, abm_type: p.abm_type, site_id: p.site_id, site_name: p.site_name }),
  ), [d?.scatter, passes]);

  /** Distribution follows band/abm/site drill-downs by rebuilding from top_sites/scatter (both are per-site). */
  const filteredDistribution = useMemo<BandCount[]>(() => {
    if (!hasDrill) return d?.risk_distribution || [];
    const buckets: Record<string, number> = {};
    filteredTop.forEach(p => { buckets[p.band] = (buckets[p.band] || 0) + 1; });
    // If top_sites was empty (edge case) fall back to scatter which is full.
    if (Object.keys(buckets).length === 0) filteredScatter.forEach(p => { buckets[p.band] = (buckets[p.band] || 0) + 1; });
    return BANDS.map(b => ({ band: b, count: buckets[b] || 0 })).filter(x => x.count > 0);
  }, [hasDrill, d?.risk_distribution, filteredTop, filteredScatter]);

  /** ABM stack: hide rows that don't match `abm_type` drill; when only `band` drill, keep rows but zero the other bands. */
  const filteredAbm = useMemo<AbmRow[]>(() => {
    const src = d?.abm_types || [];
    return src
      .filter(r => (drill.abm_type ? r.abm_type === drill.abm_type : true))
      .map(r => {
        if (!drill.band) return r;
        const zeroed = { ...r, Low: 0, Medium: 0, High: 0, Critical: 0 } as AbmRow;
        (zeroed as unknown as Record<string, number>)[drill.band] = (r as unknown as Record<string, number>)[drill.band] || 0;
        return zeroed;
      });
  }, [d?.abm_types, drill.abm_type, drill.band]);

  /** KPI recompute — from filtered scatter (holds cash + score per site). */
  const kpis = useMemo<KPIs>(() => {
    if (!d) return { site_count: 0, total_cash_exposure: 0, avg_cash_exposure: 0, avg_risk_score: 0, high_critical_count: 0, high_critical_pct: 0 };
    if (!hasDrill) return d.kpis;
    const rows = filteredScatter;
    const total_cash = rows.reduce((a, r) => a + (r.x || 0), 0);
    const scores = rows.map(r => r.y || 0);
    const site_count = rows.length;
    const highCrit = rows.filter(r => r.band === 'High' || r.band === 'Critical').length;
    return {
      site_count,
      total_cash_exposure: Math.round(total_cash),
      avg_cash_exposure: site_count ? Math.round(total_cash / site_count) : 0,
      avg_risk_score: site_count ? Number((scores.reduce((a, b) => a + b, 0) / site_count).toFixed(1)) : 0,
      high_critical_count: highCrit,
      high_critical_pct: site_count ? Number(((highCrit / site_count) * 100).toFixed(1)) : 0,
    };
  }, [d, hasDrill, filteredScatter]);

  /* ── Drill helpers ───────────────────────────────────────────── */
  const setBand = useCallback((b?: string) => setDrill(prev => ({ ...prev, band: (b && BANDS.includes(b as Band)) ? (b as Band) : undefined })), []);
  const setAbm  = useCallback((a?: string) => setDrill(prev => ({ ...prev, abm_type: a || undefined })), []);
  const setSite = useCallback((s?: string) => setDrill(prev => ({ ...prev, site_id: s || undefined })), []);
  const setCat  = useCallback((c?: string) => setDrill(prev => ({ ...prev, category: c || undefined })), []);
  const clearAll = useCallback(() => setDrill({}), []);

  /* ── Export PDF ─────────────────────────────────────────────── */
  const exportPdf = useCallback(async () => {
    if (!printRef.current) return;
    // Ensure every staged chart is mounted before we snapshot the DOM.
    setStage(2);
    await new Promise((r) => setTimeout(r, 350));
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerH = 22;
      const footerH = 10;
      const availableH = pageH - headerH - footerH - margin;

      /* ── Load the logo (once) ─────────────────────────────── */
      const logo = await new Promise<string | null>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width; c.height = img.height;
          const ctx = c.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          try { resolve(c.toDataURL('image/png')); } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = '/brand/raptor-symbol-primary.png';
      });

      const clientName = clients.find(c => c.id === filter.client_id)?.trading_name
                       || clients.find(c => c.id === filter.client_id)?.name || 'All clients';
      const siteName   = sites.find(s => s.id === filter.site_id)?.trading_name
                       || sites.find(s => s.id === filter.site_id)?.name || 'All sites';
      const dateRange  = (filter.from || filter.to) ? `${filter.from || '…'} → ${filter.to || '…'}` : 'All dates';
      const drillSummary = [
        drill.band ? `Band: ${drill.band}` : null,
        drill.abm_type ? `ABM: ${drill.abm_type}` : null,
        drill.site_id ? `Site: ${drill.site_id}` : null,
        drill.category ? `Category: ${drill.category}` : null,
      ].filter(Boolean).join(' · ');
      const generated = new Date().toLocaleString();

      /* ── Render header + footer on the current page ───────── */
      const drawChrome = (pageIdx: number, totalPages: number) => {
        // Header stripe
        pdf.setFillColor(15, 42, 74); // raptor-navy
        pdf.rect(0, 0, pageW, headerH, 'F');
        if (logo) {
          try { pdf.addImage(logo, 'PNG', margin, 4, 14, 14); } catch { /* ignore */ }
        }
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.text('Site Risk Insights Report', margin + 18, 10);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text(`Client: ${clientName}   ·   Site: ${siteName}   ·   ${dateRange}`, margin + 18, 15);
        if (drillSummary) pdf.text(`Drill-down: ${drillSummary}`, margin + 18, 19);

        // Footer
        pdf.setDrawColor(220);
        pdf.line(margin, pageH - footerH, pageW - margin, pageH - footerH);
        pdf.setTextColor(120);
        pdf.setFontSize(8);
        pdf.text('Confidential · Osprey UMA', margin, pageH - 4);
        pdf.text(`Generated ${generated}`, pageW / 2, pageH - 4, { align: 'center' });
        pdf.text(`Page ${pageIdx} of ${totalPages}`, pageW - margin, pageH - 4, { align: 'right' });
      };

      /* ── Snapshot each section and tile onto pages ────────── */
      const sections = Array.from(printRef.current.querySelectorAll<HTMLElement>('[data-pdf-section]'));
      let y = headerH + 4;
      let pageIdx = 1;

      // First pass counts pages (approx) so footer knows totalPages.
      const canvases: { canvas: HTMLCanvasElement; wMM: number; hMM: number }[] = [];
      for (const el of sections) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(el, {
          scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
        });
        const wMM = pageW - margin * 2;
        const hMM = (canvas.height * wMM) / canvas.width;
        canvases.push({ canvas, wMM, hMM });
      }

      // Estimate pages: greedy fit.
      let cursor = headerH + 4;
      let pageCount = 1;
      for (const { hMM } of canvases) {
        if (cursor + hMM > pageH - footerH - 2) { pageCount += 1; cursor = headerH + 4; }
        cursor += hMM + 4;
      }

      // Second pass: actual draw.
      drawChrome(pageIdx, pageCount);
      for (const { canvas, wMM, hMM } of canvases) {
        if (y + hMM > pageH - footerH - 2) {
          pdf.addPage();
          pageIdx += 1;
          drawChrome(pageIdx, pageCount);
          y = headerH + 4;
        }
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, y, wMM, hMM, undefined, 'FAST');
        y += hMM + 4;
      }

      const filename = `site-risk-insights_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
    } finally {
      setExporting(false);
    }
  }, [clients, sites, filter, drill]);

  /* ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-4" data-testid="analytics-page">
      {/* ── Filters + Export ─────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">ABM Site Risk Insights</CardTitle>
              <CardDescription>
                Portfolio overview, geography, and drivers of risk — all filtered together.
                Click any chart element to drill in.
              </CardDescription>
            </div>
            <Button
              variant="default" size="sm" onClick={() => void exportPdf()}
              disabled={exporting || !d} data-testid="analytics-export-pdf"
            >
              <Download size={14} /> {exporting ? 'Rendering PDF…' : 'Export PDF'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Geocoding backfill banner — only rendered when a live provider
              is configured AND we still have sites without coords. Clears
              itself once every site is mapped. */}
          {geoStatus?.provider_enabled && geoStatus.sites_missing_coords > 0 && (
            <div className="mb-3 p-3 rounded-md border border-amber-300 bg-amber-50 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="analytics-backfill-banner">
              <div className="text-xs text-raptor-navy font-body">
                <span className="font-semibold inline-flex items-center gap-1.5">
                  <Compass size={14} /> {geoStatus.sites_missing_coords} sites still missing coordinates
                </span>
                <span className="ml-2 text-raptor-navy/70">
                  Run a backfill through the active <b>{geoStatus.provider_key}</b> provider so they appear on the map.
                </span>
              </div>
              <Button size="sm" variant="default" onClick={() => void runBackfill()}
                      disabled={backfilling} data-testid="analytics-backfill-run">
                <Compass size={14} /> {backfilling ? 'Geocoding…' : 'Backfill coordinates'}
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1.5"><Label>Date range</Label>
              <Select value={datePreset(filter.from, filter.to)} onChange={(e) => setFilter({ ...filter, ...presetRange(e.target.value, filter) })} data-testid="analytics-filter-dates" className="w-40">
                <option value="all">All dates</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="thismonth">This month</option>
                <option value="custom">Custom…</option>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>From</Label>
              <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} data-testid="analytics-filter-from" />
            </div>
            <div className="space-y-1.5"><Label>To</Label>
              <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} data-testid="analytics-filter-to" />
            </div>
            <SearchableSelect label="Broker" allLabel="All brokers" value={filter.broker_id} options={brokers} onChange={(v) => setFilter({ ...filter, broker_id: v, client_id: '', site_id: '' })} testid="analytics-filter-broker" />
            <SearchableSelect label="Client" allLabel="All clients" value={filter.client_id} options={clientOptions} onChange={(v) => setFilter({ ...filter, client_id: v, site_id: '' })} testid="analytics-filter-client" />
            <SearchableSelect label="Site" allLabel="All sites" value={filter.site_id} options={siteOptions} onChange={(v) => setFilter({ ...filter, site_id: v })} testid="analytics-filter-site" />
            <SearchableSelect label="Survey template" allLabel="All templates" value={filter.template_id} options={templates} onChange={(v) => setFilter({ ...filter, template_id: v })} testid="analytics-filter-template" />
            <Button variant="outline" size="sm" onClick={() => void load()} data-testid="analytics-refresh">
              <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
            {(filter.broker_id || filter.client_id || filter.site_id || filter.template_id || filter.from || filter.to) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilter({ broker_id: '', client_id: '', site_id: '', template_id: '', from: '', to: '' }); setDrill({}); }} data-testid="analytics-reset-filters">
                <X size={14} /> Reset filters
              </Button>
            )}
          </div>

          {/* Drill-down chip row */}
          {hasDrill && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="analytics-drilldown-row">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-raptor-navy/70">
                <Filter size={12} /> Drill-down:
              </span>
              {drill.band && <DrillChip label={`Band: ${drill.band}`} onClear={() => setBand()} testid="drill-band" />}
              {drill.abm_type && <DrillChip label={`ABM: ${drill.abm_type}`} onClear={() => setAbm()} testid="drill-abm" />}
              {drill.site_id && <DrillChip label={`Site: ${drill.site_id}`} onClear={() => setSite()} testid="drill-site" />}
              {drill.category && <DrillChip label={`Category: ${drill.category}`} onClear={() => setCat()} testid="drill-category" />}
              <Button size="sm" variant="ghost" onClick={clearAll} data-testid="drill-clear-all">
                <X size={12} /> Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Printable region starts ─────────────────────── */}
      <div ref={printRef} className="space-y-4">

        {/* ── KPI tiles ───────────────────────────────── */}
        <div data-pdf-section className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPITile label="Sites assessed" value={kpis.site_count} icon={<Layers size={14} />} />
          <KPITile label={`Total cash exposure (${currencySymbol})`} value={fmtMoney(kpis.total_cash_exposure)} icon={<Wallet size={14} />} />
          <KPITile label={`Avg cash / site (${currencySymbol})`} value={fmtMoney(kpis.avg_cash_exposure)} icon={<Wallet size={14} />} />
          <KPITile label="Avg risk score" value={(kpis.avg_risk_score ?? 0).toFixed(1)} icon={<TrendingUp size={14} />} />
          <KPITile label="High / Critical" value={`${kpis.high_critical_count} · ${(kpis.high_critical_pct).toFixed(0)}%`} icon={<AlertTriangle size={14} className="text-red-600" />} accent="text-red-600" />
        </div>

        {/* ── Row 1: Map + Risk distribution pie ─────── */}
        <div data-pdf-section className="grid md:grid-cols-3 gap-4">
          <Card className="md:col-span-2 overflow-hidden" data-testid="analytics-map-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm inline-flex items-center gap-2">
              <MapPin size={16} /> Site Location Map
              {isSingleSite && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-raptor-blue/15 text-raptor-navy" data-testid="analytics-map-satellite-chip">
                  <Satellite size={11} /> Satellite
                </span>
              )}
            </CardTitle>
              <CardDescription className="text-[11px]">
                {activeTile.name} · {isSingleSite && singleSitePoint
                  ? <>focused on <b>{singleSitePoint.site_name}</b> — property view</>
                  : <>colour-graded by risk band · {filteredMap.length} surveyed{!hasDrill && unsurveyedPoints.length ? ` · ${unsurveyedPoints.length} not yet surveyed (grey)` : ''} · click a marker to drill in</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="w-full h-[440px] relative">
                {stage >= 1 && geoLoaded ? (<MapContainer
                  preferCanvas
                  center={[-29.0, 24.5]} zoom={5} scrollWheelZoom
                  style={{ height: '100%', width: '100%' }}
                >
                  {/* Basemap adapts to whichever tile provider is enabled
                      under Settings › Integrations — Google Maps, Mapbox
                      or the OpenStreetMap default — with zero UI toggles.
                      When the operator drills into a single site we flip
                      to that provider's satellite layer so property
                      boundaries pop. The map only mounts once geoStatus has
                      resolved, so we never flash OSM→Google on first paint. */}
                  <TileLayer
                    key={`${geoStatus?.map_provider?.key || 'osm'}-${isSingleSite ? 'sat' : 'street'}`}
                    url={activeTile.url}
                    attribution={activeTile.attribution}
                    subdomains={activeTile.subdomains}
                    maxZoom={activeTile.maxZoom}
                    keepBuffer={6}
                    updateWhenZooming={false}
                    errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                  />
                  <MapAutoResize />
                  <MapFocuser point={singleSitePoint} isSingle={isSingleSite} />
                  <MarkerClusterGroup
                    chunkedLoading
                    spiderfyOnMaxZoom={false}
                    showCoverageOnHover={false}
                    maxClusterRadius={50}
                    disableClusteringAtZoom={11}
                    iconCreateFunction={(cluster) => {
                      const children = cluster.getAllChildMarkers();
                      const count = cluster.getChildCount();
                      // Pick the *worst* band in the cluster so the cluster
                      // bubble screams as loudly as the most dangerous site
                      // it contains.
                      const priority: Record<string, number> = { Critical: 4, High: 3, Medium: 2, Low: 1, Unknown: 0 };
                      let worst: string = 'Low';
                      for (const m of children) {
                        const b = (m.options && m.options.bandKey) || 'Unknown';
                        if ((priority[b] || 0) > (priority[worst] || 0)) worst = b;
                      }
                      const color = BAND_COLOR[worst] || BAND_COLOR.Unknown;
                      const size = count < 10 ? 32 : count < 100 ? 40 : count < 500 ? 48 : 56;
                      const html = `
                        <div class="raptor-cluster" style="
                          background: ${color};
                          width: ${size}px; height: ${size}px; line-height: ${size}px;
                        ">${count}</div>`;
                      return L.divIcon({
                        html,
                        className: 'raptor-cluster-wrap',
                        iconSize: L.point(size, size, true),
                      });
                    }}
                  >
                    {mapMarkers.map((p, i) => (
                      <CircleMarker
                        key={`${p.lat}-${p.lng}-${i}`}
                        center={[p.lat, p.lng]}
                        radius={p.unsurveyed ? 6 : 7 + Math.min(10, p.score / 10)}
                        // Custom option — surfaced back to the cluster icon
                        // renderer above so it can pick the worst band.
                        // @ts-expect-error non-standard Leaflet option key
                        bandKey={p.unsurveyed ? 'Unknown' : p.band}
                        pathOptions={{
                          color: p.unsurveyed ? BAND_COLOR.Unknown : (BAND_COLOR[p.band] || BAND_COLOR.Unknown),
                          fillColor: p.unsurveyed ? BAND_COLOR.Unknown : (BAND_COLOR[p.band] || BAND_COLOR.Unknown),
                          fillOpacity: p.unsurveyed ? 0.45 : 0.65, weight: 1.5,
                          dashArray: p.unsurveyed ? '3 3' : undefined,
                        }}
                        // PERF: no <Popup>/<Tooltip> React children per marker —
                        // with 2 500+ markers that meant 5 000+ extra React
                        // portals mounting synchronously (the "page freezes on
                        // entry" bug). Tooltip is now bound lazily on hover; the
                        // click opens the full drill-in modal (Popup was redundant).
                        eventHandlers={{
                          mouseover: (e) => {
                            const layer = e.target as L.CircleMarker;
                            if (!layer.getTooltip()) {
                              const html = p.unsurveyed
                                ? `<div style="font-size:11px"><div style="font-weight:600">${p.site_name}</div><div style="opacity:.7">Not yet surveyed</div></div>`
                                : `<div style="font-size:11px"><div style="font-weight:600">${p.site_name}</div><div style="opacity:.7">${p.band} · Score ${p.score.toFixed(1)}</div></div>`;
                              layer.bindTooltip(html, { direction: 'top', offset: [0, -6], opacity: 0.95, className: 'raptor-map-tooltip' });
                            }
                            layer.openTooltip();
                          },
                          click: () => {
                            // A real site_id + coords open the full insight
                            // modal. Un-surveyed pins open it too — showing the
                            // aerial + street view with a "not yet surveyed"
                            // note so the operator can still verify the location.
                            if (p.site_id && Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
                              setInsightSite({
                                site_id: p.site_id,
                                site_name: p.site_name,
                                lat: p.lat,
                                lng: p.lng,
                                band: p.band,
                                score: p.score,
                                unsurveyed: p.unsurveyed,
                              });
                            } else if (!p.unsurveyed) {
                              setSite(p.site_id || p.site_name);
                            }
                          },
                        }}
                      />
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>) : (
                  <div className="w-full h-full grid place-items-center text-sm text-raptor-navy/40 bg-raptor-neutral/20" data-testid="analytics-map-loading">Loading map…</div>
                )}
                {/* Legend overlay — pinned so it never overlaps chart title/desc. */}
                <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur rounded-md shadow-lg border border-border/60 p-2 text-[10px] font-body">
                  <div className="uppercase text-raptor-navy/60 mb-1 tracking-wide">Risk band</div>
                  {BANDS.map((b) => (
                    <button
                      key={b} type="button"
                      onClick={() => setBand(drill.band === b ? undefined : b)}
                      className={`flex items-center gap-2 py-0.5 w-full text-left hover:opacity-80 transition-opacity ${drill.band === b ? 'font-bold' : ''}`}
                      data-testid={`map-legend-band-${b}`}
                    >
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BAND_COLOR[b] }} />
                      <span className="font-tabular">{b}</span>
                    </button>
                  ))}
                  {!hasDrill && unsurveyedPoints.length > 0 && (
                    <div className="flex items-center gap-2 py-0.5 mt-0.5 border-t border-border/50 pt-1" data-testid="map-legend-unsurveyed">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BAND_COLOR.Unknown, opacity: 0.6 }} />
                      <span className="font-tabular text-raptor-navy/70">Not surveyed</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="analytics-distribution-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Risk Rating Distribution</CardTitle>
              <CardDescription className="text-[11px]">Click a slice to filter — share of portfolio in each band</CardDescription>
            </CardHeader>
            <CardContent className="pb-1">
              <div className="w-full h-[440px]">
                <ResponsiveContainer>
                  <PieChart margin={{ top: 8, right: 8, bottom: 32, left: 8 }}>
                    {/* eslint-disable */}
                    <Pie
                      data={(filteredDistribution || []) as unknown as Array<Record<string, unknown>>}
                      dataKey="count" nameKey="band" cx="50%" cy="45%"
                      isAnimationActive={false}
                      outerRadius={110} innerRadius={55} paddingAngle={2}
                      // Only draw the leader-line label when the slice is large enough
                      // to fit it — otherwise let the bottom Legend carry the label.
                      // eslint-disable-next-line
                      label={((entry: any) => {
                        const total = (filteredDistribution || []).reduce((a, b) => a + (b.count || 0), 0) || 1;
                        const pct = (entry.count || 0) / total;
                        return pct >= 0.06 ? `${entry.band}: ${entry.count}` : '';
                      }) as any}
                      // eslint-disable-next-line
                      onClick={(e: any) => setBand(drill.band === e?.band ? undefined : e?.band)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(filteredDistribution || []).map((entry) => (
                        <Cell key={entry.band} fill={BAND_COLOR[entry.band] || '#94A3B8'}
                              stroke={drill.band && drill.band !== entry.band ? '#ffffff' : undefined}
                              strokeWidth={drill.band && drill.band !== entry.band ? 2 : 1}
                              fillOpacity={drill.band && drill.band !== entry.band ? 0.35 : 1}
                        />
                      ))}
                    </Pie>
                    {/* eslint-enable */}
                    <RTooltip />
                    <Legend
                      verticalAlign="bottom" height={24} iconType="circle"
                      wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                      onClick={(o) => setBand(drill.band === (o as { value?: string }).value ? undefined : (o as { value?: string }).value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Row 2: Top-10 + Cash vs Score scatter ── */}
        <div data-pdf-section className="grid md:grid-cols-2 gap-4">
          <Card data-testid="analytics-top10-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm inline-flex items-center gap-2"><AlertTriangle size={16} className="text-red-600" /> Top 10 Highest-Risk Sites</CardTitle>
              <CardDescription className="text-[11px]">Ordered by weighted risk score, descending — click a row to drill in</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs font-body">
                <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70">
                  <tr><th className="py-1.5 px-2 text-left">#</th><th className="py-1.5 px-2 text-left">Site</th><th className="py-1.5 px-2 text-left">Town</th><th className="py-1.5 px-2 text-left">ABM</th><th className="py-1.5 px-2 text-left">Band</th><th className="py-1.5 px-2 text-right">Score</th><th className="py-1.5 px-2 text-right">Cash</th></tr>
                </thead>
                <tbody>
                  {filteredTop.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-raptor-navy/50">No assessments match this drill-down.</td></tr>}
                  {filteredTop.map((s, i) => (
                    <tr
                      key={`${s.site_id || s.site_name}-${i}`}
                      className={`border-t border-border/40 cursor-pointer transition-colors ${drill.site_id === (s.site_id || s.site_name) ? 'bg-raptor-blue/10' : 'hover:bg-raptor-neutral/40'}`}
                      onClick={() => setSite(drill.site_id === (s.site_id || s.site_name) ? undefined : (s.site_id || s.site_name))}
                      data-testid={`analytics-top10-row-${i}`}
                    >
                      <td className="py-1.5 px-2 font-tabular text-raptor-navy/60">{i + 1}</td>
                      <td className="py-1.5 px-2 font-semibold text-raptor-navy">{s.site_name}</td>
                      <td className="py-1.5 px-2">{s.town || '—'}</td>
                      <td className="py-1.5 px-2 text-[10px] text-raptor-navy/70">{s.abm_type}</td>
                      <td className="py-1.5 px-2">{bandBadge(s.band)}</td>
                      <td className="py-1.5 px-2 font-tabular text-right">{s.score.toFixed(1)}</td>
                      <td className="py-1.5 px-2 font-tabular text-right">{fmtMoney(s.cash_exposure)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card data-testid="analytics-cashscore-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Cash Exposure vs. Total Risk Score</CardTitle>
              <CardDescription className="text-[11px]">One dot per site — click a dot to drill</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[400px]">
                {stage >= 2 ? (
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 10, right: 30, bottom: 56, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="x" type="number" name="Cash exposure" tickFormatter={(v: number) => fmtMoney(v)} label={{ value: `Cash Exposure (${currencySymbol})`, position: 'insideBottom', offset: -4, style: { fontSize: 11 } }} />
                    <YAxis dataKey="y" type="number" name="Risk score" label={{ value: 'Risk Score', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                    <RTooltip content={<ScatterTooltip fmt={fmtMoney} />} />
                    {BANDS.map((b) => (
                      <Scatter
                        key={b} name={b}
                        data={filteredScatter.filter(p => p.band === b) as unknown as object[]}
                        fill={BAND_COLOR[b]}
                        isAnimationActive={false}
                        onClick={(e: unknown) => {
                          const p = (e as { payload?: ScatterPoint })?.payload;
                          if (p) setSite(drill.site_id === (p.site_id || p.site_name) ? undefined : (p.site_id || p.site_name));
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                    <Legend
                      verticalAlign="bottom" iconType="circle"
                      wrapperStyle={{ paddingTop: 12, fontSize: 11 }}
                      onClick={(o) => setBand(drill.band === (o as { value?: string }).value ? undefined : (o as { value?: string }).value)}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
                ) : (
                  <div className="w-full h-full grid place-items-center text-xs text-raptor-navy/40" data-testid="analytics-scatter-loading">Loading chart…</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Row 3: ABM types + Worst categories ─── */}
        <div data-pdf-section className="grid md:grid-cols-2 gap-4">
          <Card data-testid="analytics-abm-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">ABM Type Analysis</CardTitle>
              <CardDescription className="text-[11px]">Click a bar to filter by ABM type — coloured by risk band</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[400px]">
                <ResponsiveContainer>
                  <BarChart data={filteredAbm} layout="vertical" margin={{ left: 20, right: 30, top: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="abm_type" type="category" width={130} style={{ fontSize: 11 }} />
                    <RTooltip />
                    <Legend
                      verticalAlign="bottom" iconType="circle"
                      wrapperStyle={{ paddingTop: 8, fontSize: 11 }}
                      onClick={(o) => setBand(drill.band === (o as { value?: string }).value ? undefined : (o as { value?: string }).value)}
                    />
                    {BANDS.map((b) => (
                      <Bar
                        key={b} dataKey={b} stackId="a" fill={BAND_COLOR[b]}
                        cursor="pointer"
                        isAnimationActive={false}
                        // eslint-disable-next-line
                        onClick={(e: any) => {
                          const abm = e?.abm_type as string | undefined;
                          if (abm) {
                            setAbm(drill.abm_type === abm ? undefined : abm);
                            setBand(drill.band === b ? undefined : b);
                          }
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="analytics-worst-cats-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Most Frequent &quot;Bad&quot; Answer Categories</CardTitle>
              <CardDescription className="text-[11px]">Click a bar to focus this category</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[400px]">
                <ResponsiveContainer>
                  <BarChart data={d?.worst_categories || []} layout="vertical" margin={{ left: 30, right: 50, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="category" type="category" width={170} style={{ fontSize: 11 }} />
                    <RTooltip />
                    <Bar
                      dataKey="flagged_count" fill="#EF4444" radius={[0, 4, 4, 0]}
                      cursor="pointer"
                      isAnimationActive={false}
                      // eslint-disable-next-line
                      onClick={(e: any) => {
                        const cat = e?.category as string | undefined;
                        if (cat) setCat(drill.category === cat ? undefined : cat);
                      }}
                    >
                      <LabelList dataKey="flagged_count" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Row 4: Security focus ────────────────── */}
        <Card data-pdf-section data-testid="analytics-security-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm inline-flex items-center gap-2"><Shield size={16} /> Security Section Focus</CardTitle>
            <CardDescription className="text-[11px]">Pass rate across all SEC.* questions · Top 10 SEC questions ranked by &quot;No&quot; answers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="p-3 bg-raptor-neutral/40 rounded-md">
                  <div className="text-[10px] uppercase text-raptor-navy/60">Security pass rate</div>
                  <div className="text-3xl font-bold font-tabular text-raptor-navy">{(d?.security_focus.pass_rate_pct ?? 0).toFixed(1)}%</div>
                  <div className="text-xs text-raptor-navy/60 mt-1 font-body">
                    {d?.security_focus.totals.yes ?? 0} Yes · {d?.security_focus.totals.no ?? 0} No · {d?.security_focus.totals.na ?? 0} N/A
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 h-[340px]">
                <ResponsiveContainer>
                  <BarChart data={d?.security_focus.per_question || []} layout="vertical" margin={{ left: 30, right: 40, top: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="question_code" type="category" width={210} style={{ fontSize: 10 }} />
                    <RTooltip />
                    <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ paddingTop: 8, fontSize: 11 }} />
                    <Bar dataKey="yes" stackId="s" fill={BAND_COLOR.Low} name="Yes" isAnimationActive={false} />
                    <Bar dataKey="no" stackId="s" fill={BAND_COLOR.High} name="No" isAnimationActive={false} />
                    <Bar dataKey="na" stackId="s" fill={BAND_COLOR.Unknown} name="N/A" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Site-level drill-in modal — opened from the map. Kept outside
          the printable area so it never leaks into a PDF export. */}
      {insightSite && (
        <SiteInsightsModal
          target={insightSite}
          mapProvider={geoStatus?.map_provider}
          unsurveyed={insightSite.unsurveyed}
          onClose={() => setInsightSite(null)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 * Sub-components
 * ═══════════════════════════════════════════════════════════════════════════ */


/**
 * MapFocuser — helper that lives inside the <MapContainer> and reacts
 * to the "single site" signal by animating the camera to that point.
 *
 * Because leaflet's imperative API is only reachable via ``useMap``, we
 * can't call `map.flyTo` from the parent component that owns the
 * MapContainer. This component solves that: it sits inside the map
 * tree, gets the imperative handle, and reacts to `isSingle`/`point`
 * prop changes.
 */
/**
 * MapAutoResize — fixes Leaflet's "gray tiles" problem. Because the map is
 * mounted after a staged delay (and inside a flex card), Leaflet can init
 * before the container has its final size and only request a few tiles. We
 * force a size recompute once the map is ready and again after layout settles.
 */
const MapAutoResize: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const fix = () => map.invalidateSize({ animate: false });
    const t1 = setTimeout(fix, 60);
    const t2 = setTimeout(fix, 350);
    const t3 = setTimeout(fix, 900);
    window.addEventListener('resize', fix);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); window.removeEventListener('resize', fix); };
  }, [map]);
  return null;
};

const MapFocuser: React.FC<{ point?: MapPoint; isSingle: boolean }> = ({ point, isSingle }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (isSingle && point && typeof point.lat === 'number' && typeof point.lng === 'number') {
      // Zoom 18 gives roof-level detail at ABM/site scale without
      // requesting tiles the provider might not have.
      map.flyTo([point.lat, point.lng], 18, { duration: 0.9 });
    } else {
      // Nothing selected — return to the country-level portfolio view.
      map.flyTo([-29.0, 24.5], 5, { duration: 0.9 });
    }
  }, [map, isSingle, point?.lat, point?.lng]);
  return null;
};

const KPITile: React.FC<{ label: string; value: number | string; icon?: React.ReactNode; accent?: string }> = ({ label, value, icon, accent }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-[10px] uppercase text-raptor-navy/60 inline-flex items-center gap-1.5 leading-tight">
        {icon}<span className="truncate">{label}</span>
      </div>
      <div className={`font-tabular text-2xl font-bold mt-1 ${accent || 'text-raptor-navy'}`}>{value}</div>
    </CardContent>
  </Card>
);

const DrillChip: React.FC<{ label: string; onClear: () => void; testid?: string }> = ({ label, onClear, testid }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-raptor-blue/15 text-raptor-navy text-[11px] font-semibold border border-raptor-blue/30"
    data-testid={testid}
  >
    {label}
    <button
      type="button"
      className="inline-flex items-center rounded-full hover:bg-raptor-blue/20 p-0.5"
      onClick={onClear}
      aria-label={`Clear ${label}`}
    >
      <X size={11} />
    </button>
  </span>
);

// eslint-disable-next-line
const ScatterTooltip: React.FC<any> = ({ active, payload, fmt }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload as ScatterPoint;
  const money = (fmt as ((n: number) => string) | undefined) || ((n: number) => `${n}`);
  return (
    <div className="bg-white border border-border/60 shadow rounded p-2 text-xs font-body">
      <div className="font-semibold text-raptor-navy">{p.site_name}</div>
      <div>Cash: <span className="font-tabular">{money(p.x)}</span></div>
      <div>Score: <span className="font-tabular">{p.y.toFixed(1)}</span></div>
      <div>Band: {bandBadge(p.band)}</div>
    </div>
  );
};

/* ── Date-range presets ───────────────────────────────────────────── */
function _iso(d: Date): string { return d.toISOString().slice(0, 10); }
function presetRange(key: string, cur: { from: string; to: string }): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'all') return { from: '', to: '' };
  if (key === 'custom') return { from: cur.from, to: cur.to };
  if (key === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: _iso(y), to: _iso(y) }; }
  if (key === 'last7') { const s = new Date(today); s.setDate(s.getDate() - 6); return { from: _iso(s), to: _iso(today) }; }
  if (key === 'last30') { const s = new Date(today); s.setDate(s.getDate() - 29); return { from: _iso(s), to: _iso(today) }; }
  if (key === 'thismonth') return { from: _iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: _iso(today) };
  return { from: cur.from, to: cur.to };
}
function datePreset(from: string, to: string): string {
  if (!from && !to) return 'all';
  for (const k of ['yesterday', 'last7', 'last30', 'thismonth']) {
    const r = presetRange(k, { from: '', to: '' });
    if (r.from === from && r.to === to) return k;
  }
  return 'custom';
}

/**
 * SearchableSelect — alphabetical, type-to-filter dropdown with an "All"
 * default. Options are expected pre-sorted. Closes on outside click / Esc.
 */
const SearchableSelect: React.FC<{
  label: string; allLabel: string; value: string;
  options: Lookup[]; onChange: (v: string) => void; testid: string;
}> = ({ label, allLabel, value, options, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const selected = options.find(o => o.id === value);
  const selectedLabel = selected ? (selected.trading_name || selected.name) : allLabel;
  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter(o => (o.trading_name || o.name || '').toLowerCase().includes(ql)) : options;
  return (
    <div className="space-y-1.5" ref={ref}>
      <Label>{label}</Label>
      <div className="relative">
        <button type="button" onClick={() => setOpen(o => !o)} data-testid={testid}
          className="w-56 h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-left truncate flex items-center justify-between gap-2 hover:border-raptor-blue/60">
          <span className={`truncate ${value ? 'text-raptor-navy' : 'text-raptor-navy/50'}`}>{selectedLabel}</span>
          <Filter size={13} className="text-raptor-navy/40 shrink-0" />
        </button>
        {open && (
          <div className="absolute z-[1000] mt-1 w-56 bg-white border border-border/60 rounded-md shadow-lg" data-testid={`${testid}-panel`}>
            <div className="p-2 border-b border-border/50">
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid={`${testid}-search`} className="h-8" />
            </div>
            <div className="max-h-64 overflow-auto py-1">
              <button type="button" onClick={() => { onChange(''); setOpen(false); setQ(''); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-raptor-neutral/40 ${!value ? 'font-semibold text-raptor-navy' : 'text-raptor-navy/70'}`}>{allLabel}</button>
              {filtered.map(o => (
                <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQ(''); }}
                  className={`w-full text-left px-3 py-1.5 text-sm truncate hover:bg-raptor-neutral/40 ${value === o.id ? 'font-semibold text-raptor-navy bg-raptor-blue/10' : 'text-raptor-navy/80'}`}>
                  {o.trading_name || o.name}
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-2 text-xs text-raptor-navy/40">No matches</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AnalyticsPage: React.FC = () => (
  <CustomiseHost page="analytics" defaultContent={<DefaultAnalyticsPage />} />
);

export default AnalyticsPage;
export { AnalyticsPage };
